import React, { useEffect, useMemo, useRef, useState } from "react";
import "./chat-modal.scss";
import { observer } from "mobx-react-lite";
import { useAppStore } from "store";
import LoadingIndicator from "../loading-indicator/loading-indicator";
import { mobileCheck } from "helpers/device.helper";
import { getAvatarTitle } from "helpers/string.helper";
import { SiteSettingsKeys } from "models/dto/ZoomiLxp/Utilities/Constants/SiteSettingsKeys";
import { RequestOrigins } from "models/dto/ZoomiLxp/Utilities/Enumerations/RequestOrigins";
import ChatBotMessage from "./chat-message";

type ChatModalProps = {
	userId?: number;
	origin: RequestOrigins;
	courseId?: number;
	contentId?: number;
};

export const ChatModal: React.FC<ChatModalProps> = observer(({ userId, origin, courseId, contentId }) => {
	const { chatBotStore, usersStore, settingsStore } = useAppStore();
	const [isOpen, setIsOpen] = useState(false);
	const [input, setInput] = useState("");
	const [historyLoaded, setHistoryLoaded] = useState(false);

	const hasMessages = chatBotStore.messages.length > 0;

	const currentUser = usersStore.currentUserInfo;

	const isMobile = useMemo(() => mobileCheck(), []);

	const bottomRef = useRef<HTMLDivElement>(null);

	//temporal soultion before implementation of 1:1 chat between users

	const userAvatar = () => {
		return currentUser?.userPicture?.url;
	};

	const aiAvatar = () => {
		const banner = settingsStore.publicSettings.AITutor?.[SiteSettingsKeys.AITutorBanner];
		return banner && banner.trim() !== "" ? banner : settingsStore.publicSettings.Images?.[SiteSettingsKeys.Logo];
	};

	const llmName = () => {
		return settingsStore.publicSettings.AITutor?.[SiteSettingsKeys.AITutorName] ?? "Ai Tutor";
	};

	const userAvatarTitle = () => {
		return getAvatarTitle(currentUser?.firstName ?? "", currentUser?.lastName ?? "");
	};

	const aiAvatarTitle = () => {
		return "AI";
	};

	const handleSend = () => {
		chatBotStore.sendMessage(input, origin, userId, courseId, contentId);
		setInput("");
	};

	useEffect(() => {
		let isCancelled = false;

		const syncChatContext = async () => {
			if (!userId) return;

			const isContextChanged =
				historyLoaded &&
				(chatBotStore.currentOrigin !== origin ||
					chatBotStore.currentCourseId !== courseId ||
					chatBotStore.currentContentId !== contentId);

			if (isContextChanged) {
				await chatBotStore.disposeSignalR();
				if (!isCancelled) setHistoryLoaded(false);
				return;
			}

			if (!historyLoaded) {
				await chatBotStore.loadHistory(origin, userId, courseId, contentId);
				if (!isCancelled) setHistoryLoaded(true);
			}
		};

		void syncChatContext();

		return () => {
			isCancelled = true;
		};
	}, [historyLoaded, userId, origin, courseId, contentId, chatBotStore]);

	useEffect(() => {
		bottomRef.current?.scrollIntoView({ behavior: "smooth" });
	}, [chatBotStore.messages.length, chatBotStore.isStreaming]);

	useEffect(() => {
		requestAnimationFrame(() => {
			bottomRef.current?.scrollIntoView({ behavior: "smooth" });
		});
	}, [isOpen]);

	useEffect(
		() => {
			return () => {
				void chatBotStore.disposeSignalR();
			};
		},
		//closing SignalR connection on component unmount
		//eslint-disable-next-line react-hooks/exhaustive-deps
		[]
	);

	return (
		<div className={`chatbot-container ${isOpen ? "open" : ""} ${isMobile ? "mobile-chat" : ""}`}>
			<div className="chatbot-header" onClick={() => setIsOpen((prev) => !prev)}>
				<span>{llmName()}</span>
				<span className="chevron">{isOpen ? "▾" : "▴"}</span>
			</div>

			{isOpen && (
				<div className="chatbot-body">
					{!chatBotStore.isLoading && historyLoaded && (
						<div className="chatbot-messages">
							{hasMessages ? (
								chatBotStore.messages.map((msg) => {
									const isUser = msg.from === "user";
									const avatarUrl = isUser ? userAvatar() : aiAvatar();
									const avatarTitle = isUser ? userAvatarTitle() : aiAvatarTitle();
									return (
										<ChatBotMessage
											msg={msg}
											avatarTitle={avatarTitle}
											avatarUrl={avatarUrl}
											isStreaming={chatBotStore.isMessageStreaming(msg.id)}
											key={msg.id}
											userType={msg.from}
										/>
									);
								})
							) : (
								<ChatBotMessage
									msg={chatBotStore.placeholderMessage}
									avatarTitle={aiAvatarTitle()}
									avatarUrl={aiAvatar()}
									isStreaming={false}
									key={chatBotStore.placeholderMessage.id}
									userType="bot"
								/>
							)}
							<div ref={bottomRef} />
						</div>
					)}

					{(chatBotStore.isLoading || !historyLoaded) && (
						<div className="chatbot-loading">
							<LoadingIndicator loading={chatBotStore.isLoading || !historyLoaded} />
						</div>
					)}

					<div className="chatbot-input">
						<input
							type="text"
							value={input}
							onChange={(e) => setInput(e.target.value)}
							onKeyDown={(e) => {
								if (e.key === "Enter") {
									handleSend();
								}
							}}
							placeholder="Write a message..."
							disabled={chatBotStore.isLoading || chatBotStore.isStreaming}
						/>
						<button onClick={handleSend} disabled={chatBotStore.isLoading || chatBotStore.isStreaming}>
							Send
						</button>
					</div>
				</div>
			)}
		</div>
	);
});
